import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle, Bank, Sparkle, Warning } from "@phosphor-icons/react";

/**
 * Modal that opens when a creator clicks "Autorizar uso comercial" on a song.
 * Collects the data needed to pay royalties (60% of B2B revenue) and saves it
 * to the user profile. Reused across sessions: if the user already filled the
 * form once, we prefill the fields.
 */
export function CommercialAuthDialog({ songId, songTitle, open, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [ofAge, setOfAge] = useState(false);
  const [payoutFrequency, setPayoutFrequency] = useState("quarterly");
  const [submitting, setSubmitting] = useState(false);

  // Prefill from previous authorization
  useEffect(() => {
    if (!open) return;
    setStep(1);
    (async () => {
      try {
        const { data } = await api.get("/songs/creator-payout");
        if (data?.payout) {
          setFullName(data.payout.full_name || "");
          setAddress(data.payout.address || "");
          setBankName(data.payout.bank_name || "");
          setBankAccount(data.payout.bank_account || "");
          setPayoutFrequency(data.payout.payout_frequency || "quarterly");
        }
      } catch (e) {
        console.warn("Failed to load creator payout:", e);
      }
    })();
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!ofAge) return toast.error("Debes confirmar que eres mayor de edad");
    if (fullName.trim().length < 5) return toast.error("Escribe tu nombre completo");
    if (address.trim().length < 8) return toast.error("Escribe tu domicilio completo");
    if (!bankName.trim()) return toast.error("Escribe el nombre del banco");
    const digits = bankAccount.replace(/\D/g, "");
    if (digits.length < 10) return toast.error("La cuenta o CLABE no es válida");
    setSubmitting(true);
    try {
      await api.post(`/songs/${songId}/authorize-commercial`, {
        full_name: fullName.trim(),
        address: address.trim(),
        bank_name: bankName.trim(),
        bank_account: digits,
        of_age: ofAge,
        payout_frequency: payoutFrequency,
      });
      toast.success("¡Listo! Tu canción participa en el TOP comercial.");
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando autorización");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="commercial-auth-dialog">
      <div className="relative max-w-lg w-full rounded-3xl border border-[#B4F34A]/30 bg-gradient-to-b from-[#0e1d10] to-[#0a0a0a] p-6 sm:p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70" data-testid="commercial-auth-close">
          <X size={16} weight="bold" />
        </button>

        {step === 1 ? (
          <>
            <div className="w-14 h-14 rounded-full bg-[#B4F34A] flex items-center justify-center mb-4">
              <Sparkle size={26} weight="fill" className="text-black" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Autorizar uso comercial</h2>
            <p className="text-sm text-white/60 mb-5">Canción: <span className="text-white font-semibold">{songTitle}</span></p>

            <div className="space-y-3 text-sm text-white/80 mb-6">
              <p>Al autorizar, nos das permiso para:</p>
              <ul className="space-y-2">
                <li className="flex gap-2"><CheckCircle weight="fill" size={18} className="text-[#B4F34A] shrink-0 mt-0.5" /><span>Incluir tu canción en la selección del TOP mensual.</span></li>
                <li className="flex gap-2"><CheckCircle weight="fill" size={18} className="text-[#B4F34A] shrink-0 mt-0.5" /><span>Si entra al TOP, integrarla al repertorio que vendemos a restaurantes, bares y cafeterías.</span></li>
                <li className="flex gap-2"><CheckCircle weight="fill" size={18} className="text-[#B4F34A] shrink-0 mt-0.5" /><span>Pagarte regalías cada 3, 6 o 12 meses según tú elijas.</span></li>
              </ul>
              <div className="rounded-xl bg-[#FFB800]/5 border border-[#FFB800]/20 p-4 mt-3">
                <p className="font-semibold text-[#FFB800] mb-1">Reparto: 60/40</p>
                <p className="text-xs leading-relaxed text-white/70">
                  El 60% de lo recaudado en cada periodo se prorratea entre las canciones vigentes del repertorio comercial. Si tu canción es de las más reproducidas, te toca una porción mayor.
                  Cada 3 meses se renueva el repertorio: las más tocadas se quedan.
                </p>
              </div>
              <p className="text-xs text-white/40">
                Lee la cláusula completa en <Link to="/terminos" className="underline hover:text-white/70" onClick={onClose}>Términos del servicio</Link>.
              </p>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-3.5 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold active:scale-95 transition-all"
              data-testid="commercial-auth-continue"
            >
              Sí, autorizo · Continuar
            </button>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#FFB800]/15 flex items-center justify-center mb-4">
              <Bank weight="duotone" size={24} className="text-[#FFB800]" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-1">Datos para tus regalías</h2>
            <p className="text-sm text-white/50 mb-5">Solo lo usamos para depositarte. Nunca lo compartimos.</p>

            <div className="space-y-3">
              <Field label="Nombre completo">
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Como aparece en tu identificación" className="input" data-testid="commercial-auth-name" />
              </Field>
              <Field label="Domicilio completo">
                <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Calle, número, colonia, ciudad, estado, CP" className="input" data-testid="commercial-auth-address" />
              </Field>
              <Field label="Banco">
                <input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="BBVA, Banamex, Santander, etc." className="input" data-testid="commercial-auth-bank" />
              </Field>
              <Field label="CLABE o número de cuenta">
                <input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} placeholder="18 dígitos para CLABE" className="input font-mono" data-testid="commercial-auth-account" />
              </Field>
              <Field label="Frecuencia de pago">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { k: "quarterly", label: "Cada 3 meses" },
                    { k: "semestral", label: "Cada 6 meses" },
                    { k: "yearly", label: "Anual" },
                  ].map(o => {
                    const on = payoutFrequency === o.k;
                    return (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setPayoutFrequency(o.k)}
                        className={`py-2 rounded-xl text-xs font-semibold border transition-all ${on ? "bg-[#B4F34A] border-[#B4F34A] text-black" : "bg-[#121212] border-white/10 text-white/70 hover:border-white/30"}`}
                        data-testid={`payout-freq-${o.k}`}
                      >{o.label}</button>
                    );
                  })}
                </div>
              </Field>
              <label className="flex items-start gap-3 px-3 py-3 rounded-xl bg-[#FF4B00]/5 border border-[#FF4B00]/20 cursor-pointer">
                <input type="checkbox" checked={ofAge} onChange={(e) => setOfAge(e.target.checked)} className="mt-1 w-4 h-4 accent-[#B4F34A]" data-testid="commercial-auth-of-age" />
                <span className="text-xs text-white/80 leading-relaxed">
                  <strong className="text-white">Confirmo bajo protesta de decir verdad que soy mayor de edad</strong> y que los datos bancarios son míos. Acepto los <Link to="/terminos" className="underline" onClick={onClose}>Términos del servicio</Link>.
                </span>
              </label>
            </div>

            <div className="mt-5 rounded-xl bg-white/5 border border-white/10 p-3 flex gap-2">
              <Warning size={16} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
              <p className="text-[11px] text-white/60 leading-relaxed">
                Estos datos se aplican a TODAS tus canciones autorizadas. Si autorizas otra, no tendrás que llenarlos de nuevo.
              </p>
            </div>

            <div className="mt-5 flex gap-2">
              <button onClick={() => setStep(1)} className="px-5 py-3 rounded-full bg-white/5 hover:bg-white/10 text-white/70 font-medium" data-testid="commercial-auth-back">
                Atrás
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="flex-1 py-3 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] disabled:opacity-40 text-black font-bold active:scale-95"
                data-testid="commercial-auth-submit"
              >
                {submitting ? "Guardando…" : "Autorizar y guardar"}
              </button>
            </div>
          </>
        )}
      </div>
      <style>{`.input{width:100%;padding:.7rem .9rem;border-radius:.75rem;background:#121212;border:1px solid rgba(255,255,255,.1);color:#fff;outline:none}.input:focus{border-color:#B4F34A}.input::placeholder{color:rgba(255,255,255,.3)}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-white/60 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
